import React, { useState, useEffect } from 'react';
import { Brain } from '@phosphor-icons/react';
import api from '@/utils/api';

/**
 * BusinessMemoryBanner
 * --------------------
 * Small reassurance pill shown at the top of any generator modal that consumes
 * the user's saved business memory. Fetches /api/business-memory/profile once
 * on mount and shows the active business name + niche.
 *
 * If no memory is saved yet, the banner renders nothing (so new users don't
 * see an empty bubble).
 */
export default function BusinessMemoryBanner({ variant = 'default', className = '' }) {
  const [memory, setMemory] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/business-memory/profile');
        // Endpoint returns { has_memory: bool, memory: {...} }
        const mem = res.data?.memory || res.data || null;
        if (!cancelled) setMemory(mem);
      } catch {
        if (!cancelled) setMemory(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;
  const hasMemory = memory && (memory.business_name || memory.niche);
  if (!hasMemory) return null;

  const name = memory.business_name || memory.niche;
  const detail = memory.target_audience ? ` · ${memory.target_audience}` : (memory.niche && memory.business_name ? ` · ${memory.niche}` : '');

  const darkClass = variant === 'light'
    ? 'bg-purple-50 border-purple-200 text-purple-700'
    : 'bg-purple-500/10 border-purple-400/30 text-purple-100';

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-medium w-fit ${darkClass} ${className}`}
      data-testid="business-memory-banner"
      title="All generators share this saved business profile. Update it in Settings → My Business."
    >
      <Brain size={12} weight="fill" />
      <span className="truncate max-w-[320px]">
        Using your saved business memory:&nbsp;
        <span className="font-semibold">{name}</span>
        <span className="opacity-70">{detail}</span>
      </span>
    </div>
  );
}
